import Add from "@mui/icons-material/Add";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Circle from "@mui/icons-material/Circle";
import Close from "@mui/icons-material/Close";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Star from "@mui/icons-material/Star";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPremiumProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridActionsCellItem,
  GridColDef,
  gridDetailPanelExpandedRowIdsSelector,
  GridRowId,
  GridRowParams,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  FacilityPilotProgramStatesQuery,
  FacilityPilotProgramStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import pluralize from "pluralize";
import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { AddPilotFacilityModal } from "./AddPilotFacilityModal";

const FACILITY_PILOT_PROGRAM_STATES_QUERY = gql(`
  query FacilityPilotProgramStates($reportingYear: Int!) {
    facilityPilotProgramStates(reportingYear: $reportingYear) {
      items {
        state
        stateName
        verifiedReportCount
        pilotFacilityCount
        totalReportingFacilities
        status
        isStateEnabled
        portalTech
        pilotFacilities {
          facilityId
          facilityName
          streetAddress1
          city
          facilityState
          tenantId
          tenantName
          isDeniedTenant
          reportId
          reportOrgStatus
          reportEncampStatus
          workflowStatus
          workflowId
          workflowTags {
            id
            name
          }
          configId
          configNotes
          configCreatedAt
          configCreatedBy
        }
      }
    }
  }
`);

const REMOVE_FACILITY_FROM_PILOT_PROGRAM_MUTATION = gql(`
  mutation RemoveFacilityFromPilotProgram($configId: ID!) {
    removeFacilityFromPilotProgram(configId: $configId)
  }
`);

const UPDATE_WORKFLOW_TAGS_BULK_MUTATION = gql(`
  mutation UpdateWorkflowTagsBulk($workflowUpdates: [WorkflowTagUpdateInput!]!) {
    updateWorkflowTagsBulk(workflowUpdates: $workflowUpdates) {
      id
      tags {
        id
        name
      }
    }
  }
`);

type StateRow =
  FacilityPilotProgramStatesQuery["facilityPilotProgramStates"]["items"][number];
type PilotFacilityRow = StateRow["pilotFacilities"][number];

const getStatusLabel = (status: FacilityPilotProgramStatus): string => {
  switch (status) {
    case FacilityPilotProgramStatus.NotStarted:
      return "Not Started";
    case FacilityPilotProgramStatus.InError:
      return "Error";
    case FacilityPilotProgramStatus.InProgress:
      return "In Progress";
    case FacilityPilotProgramStatus.Completed:
      return "Completed";
    default:
      return status;
  }
};

// Compute unique tags from all workflows in a state
const getStateTags = (row: StateRow): string[] => {
  const allTags = new Set<string>();
  row.pilotFacilities.forEach((facility) => {
    facility.workflowTags?.forEach((tag) => {
      allTags.add(tag.name);
    });
  });
  return Array.from(allTags).sort();
};

// Get status icon component
const getStatusIcon = (
  status: FacilityPilotProgramStatus,
  isStateEnabled: boolean
) => {
  // Allowed state takes precedence over all other statuses
  if (isStateEnabled) {
    return <Star fontSize="small" sx={{ color: "#FFD700", opacity: 0.9 }} />;
  }

  switch (status) {
    case FacilityPilotProgramStatus.Completed:
      return (
        <CheckCircle
          fontSize="small"
          sx={{ color: "success.light", opacity: 0.8 }}
        />
      );
    case FacilityPilotProgramStatus.InError:
      return (
        <Error fontSize="small" sx={{ color: "error.light", opacity: 0.8 }} />
      );
    case FacilityPilotProgramStatus.InProgress:
      return (
        <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />
      );
    case FacilityPilotProgramStatus.NotStarted:
    default:
      return (
        <Circle
          fontSize="small"
          sx={{ color: "text.disabled", opacity: 0.6 }}
        />
      );
  }
};

const ExpandCollapseAllHeader = () => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const expandableRowIds = useMemo(() => {
    return Object.keys(rowsLookup).filter((rowId) => {
      const row = rowsLookup[rowId] as StateRow;
      return row.pilotFacilities && row.pilotFacilities.length > 0;
    });
  }, [rowsLookup]);

  const allExpanded =
    expandableRowIds.length > 0 &&
    expandableRowIds.every((id) => expandedRowIds.has(id));

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      expandedRowIds.forEach((rowId: GridRowId) => {
        apiRef.current.toggleDetailPanel(rowId);
      });
    } else {
      expandableRowIds.forEach((rowId) => {
        if (!expandedRowIds.has(rowId)) {
          apiRef.current.toggleDetailPanel(rowId);
        }
      });
    }
  };

  return (
    <IconButton
      onClick={handleExpandCollapseAll}
      size="small"
      title={allExpanded ? "Collapse all" : "Expand all"}
    >
      {allExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

const ExpandCollapseCell = ({ row, id }: { row: StateRow; id: GridRowId }) => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const isExpanded = expandedRowIds.has(id);
  const hasPilotFacilities =
    row.pilotFacilities && row.pilotFacilities.length > 0;

  if (!hasPilotFacilities) {
    return null;
  }

  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        apiRef.current.toggleDetailPanel(id);
      }}
    >
      {isExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

const PilotWorkflowTagsCell = ({
  row,
  onOpenTagsDialog,
}: {
  row: StateRow;
  onOpenTagsDialog: (row: StateRow) => void;
}) => {
  const stateTags = getStateTags(row);
  const hasWorkflows = row.pilotFacilities.some(
    (facility) => facility.workflowId !== null
  );
  const hasNoTags = stateTags.length === 0;

  // If there are tags, show them
  if (!hasNoTags) {
    // Show max 3-4 tags, then overflow if more
    const maxVisibleTags = 3;
    const hasOverflow = stateTags.length > maxVisibleTags;
    const visibleTags = hasOverflow
      ? stateTags.slice(0, maxVisibleTags)
      : stateTags;
    const overflowCount = stateTags.length - maxVisibleTags;

    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          overflow: "hidden",
          width: "100%",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onOpenTagsDialog(row);
        }}
      >
        {visibleTags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              flexShrink: 0,
              maxWidth: 80,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        ))}
        {hasOverflow && (
          <Chip
            label={`+${overflowCount}`}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              flexShrink: 0,
              minWidth: "auto",
            }}
          />
        )}
      </Box>
    );
  }

  // If there are workflows but no tags, show "+ add tag" on hover
  if (hasWorkflows && hasNoTags) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          width: "100%",
          minHeight: 24,
        }}
      >
        <Chip
          label="+ add tag"
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onOpenTagsDialog(row);
          }}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            cursor: "pointer",
            opacity: 0,
            transition: "opacity 0.2s",
            ".MuiDataGrid-row:hover &": {
              opacity: 1,
            },
          }}
        />
      </Box>
    );
  }

  // No workflows, return null
  return null;
};

export function FacilityPilotProgram() {
  const [reportingYear, setReportingYear] = useState<number>(
    currentTierIIReportingYear
  );
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<StateRow | null>(null);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [showTagsConfirmation, setShowTagsConfirmation] = useState(false);
  const alerts = useAlerts();
  const apiRef = useGridApiRef();

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Facility Pilot Program",
    },
  ]);

  const { data, loading, refetch } = useQuery(
    FACILITY_PILOT_PROGRAM_STATES_QUERY,
    {
      variables: { reportingYear },
      fetchPolicy: "cache-and-network",
    }
  );

  const [removeMutation, { loading: removeLoading }] = useMutation(
    REMOVE_FACILITY_FROM_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Facility removed from pilot program");
        refetch();
      },
      onError: (error) => {
        alerts.error(`Failed to remove facility: ${error.message}`);
      },
    }
  );

  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [FACILITY_PILOT_PROGRAM_STATES_QUERY],
    }
  );

  const handleRemoveFacility = useCallback(
    (configId: string) => {
      if (
        window.confirm(
          "Are you sure you want to remove this facility from the pilot program?"
        )
      ) {
        removeMutation({ variables: { configId } });
      }
    },
    [removeMutation]
  );

  const handleAddFacility = useCallback((row: StateRow) => {
    setSelectedRow(row);
    setAddModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setAddModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleFacilityAdded = useCallback(() => {
    refetch();
    handleModalClose();
  }, [refetch, handleModalClose]);

  // Handle opening tags dialog for a state
  const handleOpenTagsDialog = useCallback((row: StateRow) => {
    setSelectedRow(row);
    setTagsDialogOpen(true);
  }, []);

  // Handle closing tags dialog
  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowTagsConfirmation(false);
    setSelectedRow(null);
  }, []);

  // Handle submitting tag updates
  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize(
            "workflow",
            workflowCount
          )}`
        );

        handleCloseTagsDialog();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, handleCloseTagsDialog]
  );

  // Transform selected state workflows to dialog format
  const tagsDialogRows = useMemo(() => {
    if (!selectedRow) return [];
    return selectedRow.pilotFacilities
      .filter(
        (facility): facility is PilotFacilityRow & { workflowId: string } =>
          facility.workflowId !== null
      )
      .map((facility) => ({
        id: facility.workflowId,
        tags: facility.workflowTags?.map((tag) => tag.name) || [],
      }));
  }, [selectedRow]);

  const columns: GridColDef<StateRow>[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderHeader: () => <ExpandCollapseAllHeader />,
        renderCell: ({ row, id }) => <ExpandCollapseCell row={row} id={id} />,
      },
      {
        field: "statusIcon",
        headerName: "Status",
        width: 80,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const tooltipTitle = row.isStateEnabled
            ? "State is enabled"
            : getStatusLabel(row.status);
          return (
            <Tooltip title={tooltipTitle}>
              {getStatusIcon(row.status, row.isStateEnabled)}
            </Tooltip>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="bold">
            {row.state}
          </Typography>
        ),
      },
      {
        field: "stateName",
        headerName: "State Name",
        width: 150,
      },
      {
        field: "portalTech",
        headerName: "Portal Tech",
        width: 120,
        valueGetter: (_, row) => row.portalTech ?? "-",
      },
      {
        field: "totalReportingFacilities",
        headerName: "Reporting Facilities",
        width: 140,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Reporting Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {row.totalReportingFacilities}
          </Typography>
        ),
      },
      {
        field: "verifiedReportCount",
        headerName: "Candidate Facilities",
        width: 140,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Candidate Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Tooltip title="Reports with encampStatus = PROCESSING">
            <Typography variant="body2">{row.verifiedReportCount}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "pilotFacilityCount",
        headerName: "Pilot Facilities",
        width: 120,
        type: "number",
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            fontWeight={row.pilotFacilityCount > 0 ? "bold" : "normal"}
          >
            {row.pilotFacilityCount}
          </Typography>
        ),
      },
      {
        field: "tags",
        headerName: "Pilot Workflow Tags",
        width: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <PilotWorkflowTagsCell
            row={row}
            onOpenTagsDialog={handleOpenTagsDialog}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        flex: 0.5,
        minWidth: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const hasNoCandidateReports = (row.verifiedReportCount ?? 0) === 0;
          if (hasNoCandidateReports) {
            return (
              <Button
                size="small"
                startIcon={<DoNotDisturb />}
                disabled
                sx={{
                  opacity: 0,
                  transition: "opacity 0.2s",
                  cursor: "not-allowed",
                  ".MuiDataGrid-row:hover &": {
                    opacity: 1,
                  },
                }}
              >
                No Candidates
              </Button>
            );
          }
          return (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddFacility(row);
              }}
              sx={{
                opacity: 0,
                transition: "opacity 0.2s",
                ".MuiDataGrid-row:hover &": {
                  opacity: 1,
                },
              }}
            >
              Add Pilot Facility
            </Button>
          );
        },
      },
    ],
    [handleAddFacility, handleOpenTagsDialog]
  );

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps["getDetailPanelContent"]>
  >(
    ({ row }) => {
      if (!row.pilotFacilities || row.pilotFacilities.length === 0) {
        return null;
      }
      return (
        <DetailPanelContent
          pilotFacilities={row.pilotFacilities}
          onRemove={handleRemoveFacility}
          removeLoading={removeLoading}
        />
      );
    },
    [handleRemoveFacility, removeLoading]
  );

  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridPremiumProps["getDetailPanelHeight"]>
  >(() => "auto", []);

  const handleRowClick = useCallback(
    (params: GridRowParams<StateRow>) => {
      // Only expand if there are pilot facilities to show
      if (params.row.pilotFacilities && params.row.pilotFacilities.length > 0) {
        apiRef.current?.toggleDetailPanel(params.id);
      }
    },
    [apiRef]
  );

  const rows = useMemo(
    () => data?.facilityPilotProgramStates.items ?? [],
    [data]
  );

  // Collect all existing pilot facility IDs for the modal
  const existingPilotFacilityIds = useMemo(() => {
    const ids = new Set<string>();
    rows.forEach((row) => {
      row.pilotFacilities.forEach((pf) => {
        ids.add(pf.facilityId);
      });
    });
    return ids;
  }, [rows]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (rows.length === 0) {
      return {
        totalStates: 0,
        statesEnabled: 0,
        statesEnabledPercent: 0,
        statesStarted: 0,
        statesStartedPercent: 0,
        statesCompleted: 0,
        statesCompletedPercent: 0,
        statesInError: 0,
        totalPilotFacilities: 0,
        totalVerifiedReports: 0,
      };
    }

    const totalStates = rows.length;
    const statesEnabled = rows.filter((r) => r.isStateEnabled).length;
    const statesStarted = rows.filter(
      (r) =>
        r.status === FacilityPilotProgramStatus.InProgress ||
        r.status === FacilityPilotProgramStatus.Completed
    ).length;
    const statesCompleted = rows.filter(
      (r) => r.status === FacilityPilotProgramStatus.Completed
    ).length;
    const statesInError = rows.filter(
      (r) => r.status === FacilityPilotProgramStatus.InError
    ).length;
    const totalPilotFacilities = rows.reduce(
      (acc, r) => acc + r.pilotFacilityCount,
      0
    );
    const totalVerifiedReports = rows.reduce(
      (acc, r) => acc + r.verifiedReportCount,
      0
    );

    return {
      totalStates,
      statesEnabled,
      statesEnabledPercent: Math.round((statesEnabled / totalStates) * 100),
      statesStarted,
      statesStartedPercent: Math.round((statesStarted / totalStates) * 100),
      statesCompleted,
      statesCompletedPercent: Math.round((statesCompleted / totalStates) * 100),
      statesInError,
      totalPilotFacilities,
      totalVerifiedReports,
    };
  }, [rows]);

  return (
    <Box sx={{ pt: 3, px: 3 }}>
      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 2 }}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={2}
      >
        <TierIIYearPicker
          value={reportingYear}
          onChange={(value) => {
            if (value !== null && value !== undefined) {
              const year =
                typeof value === "number" ? value : parseInt(String(value), 10);
              if (!isNaN(year)) {
                setReportingYear(year);
              }
            }
          }}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="stretch"
          flexWrap="wrap"
          rowGap={1}
        >
          {/* States Enabled mini-stat */}
          <Tooltip title="States with ALLOW_STATE workflow config enabled">
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Star
                  fontSize="small"
                  sx={{ color: "#FFD700", opacity: 0.9 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  States Enabled
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {summaryStats.statesEnabled}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesEnabledPercent}
                color="success"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States Started mini-stat */}
          <Tooltip title="States with at least one pilot facility added">
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Info
                  fontSize="small"
                  sx={{ color: "info.light", opacity: 0.8 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Pilots Started
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {summaryStats.statesStarted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesStartedPercent}
                color="info"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States Completed mini-stat */}
          <Tooltip title="States where all pilot facilities have completed workflows">
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckCircle
                  fontSize="small"
                  sx={{ color: "success.light", opacity: 0.8 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Pilots Completed
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {summaryStats.statesCompleted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesCompletedPercent}
                color="success"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States In Error mini-stat - only show if there are errors */}
          {summaryStats.statesInError > 0 && (
            <Tooltip title="States with pilot facilities in error state">
              <Paper
                variant="outlined"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  minWidth: 100,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  borderColor: "error.main",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Error
                    fontSize="small"
                    sx={{ color: "error.light", opacity: 0.8 }}
                  />
                  <Typography variant="caption" color="error" noWrap>
                    In Error
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {summaryStats.statesInError}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  color="error"
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Paper>
            </Tooltip>
          )}

          {/* Totals summary */}
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 140,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 0.25,
              bgcolor: "action.hover",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {summaryStats.totalPilotFacilities}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                pilot facilities
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {summaryStats.totalVerifiedReports.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PROCESSING reports
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      <Box sx={{ height: "calc(100vh - 280px)", minHeight: 400 }}>
        <DataGrid<StateRow>
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.state}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          onRowClick={handleRowClick}
          disableRowGrouping={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "totalReportingFacilities", sort: "desc" }],
            },
          }}
          pageSizeOptions={[25, 51, 100]}
          sx={{
            "& .MuiDataGrid-detailPanel": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row.Mui-expanded": {
              backgroundColor: "action.hover",
            },
          }}
        />
      </Box>

      <AddPilotFacilityModal
        open={addModalOpen}
        onClose={handleModalClose}
        onFacilityAdded={handleFacilityAdded}
        state={selectedRow?.state ?? null}
        reportingYear={reportingYear}
        existingPilotFacilityIds={existingPilotFacilityIds}
      />

      <ManageTagsFormDialog
        selectedRows={tagsDialogRows}
        getEntityId={(row) => row.id}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={() => setShowTagsConfirmation(false)}
        confirm={() => setShowTagsConfirmation(true)}
        open={tagsDialogOpen}
        withConfirmation={true}
        showConfirmation={showTagsConfirmation}
        onSubmit={handleSubmitTags}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="workflow-recent-tags"
        commonTags={["compliance", "engineering", "linear"]}
        TagPickerComponent={WorkflowTagPickerAdapter}
      />
    </Box>
  );
}

const DetailPanelContent = React.memo(function DetailPanelContent({
  pilotFacilities,
  onRemove,
  removeLoading,
}: {
  pilotFacilities: PilotFacilityRow[];
  onRemove: (configId: string) => void;
  removeLoading: boolean;
}) {
  const columns: GridColDef<PilotFacilityRow>[] = useMemo(
    () => [
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack>
            <Typography variant="body2">{row.facilityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.streetAddress1, row.city, row.facilityState]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "tenantName",
        headerName: "Organization",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">{row.tenantName}</Typography>
            {row.isDeniedTenant && (
              <Chip label="DENIED" color="error" size="small" />
            )}
          </Stack>
        ),
      },
      {
        field: "reportOrgStatus",
        headerName: "Report Status",
        width: 140,
        renderCell: ({ row }) => {
          if (!row.reportId) {
            return (
              <Typography variant="body2" color="text.secondary">
                No Report
              </Typography>
            );
          }
          return (
            <Chip
              label={prettyPrintEnumValue(row.reportOrgStatus)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "reportEncampStatus",
        headerName: "Encamp Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.reportEncampStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Chip
              label={prettyPrintEnumValue(row.reportEncampStatus)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.workflowStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                Not Started
              </Typography>
            );
          }
          const color =
            row.workflowStatus === "Completed"
              ? "success"
              : row.workflowStatus === "Error"
              ? "error"
              : row.workflowStatus === "InProgress"
              ? "info"
              : "default";
          return (
            <Chip
              label={prettyPrintEnumValue(row.workflowStatus)}
              size="small"
              color={color}
            />
          );
        },
      },
      {
        field: "workflowTags",
        headerName: "Workflow Tags",
        width: 200,
        flex: 1,
        renderCell: ({ row }) => {
          const tags = row.workflowTags;
          if (!tags || tags.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {tags.map((tag: { id: string; name: string }) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                    maxWidth: 80,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: "configCreatedBy",
        headerName: "Added By",
        width: 150,
        renderCell: ({ row }) => (
          <Tooltip
            title={`Added: ${new Date(row.configCreatedAt).toLocaleString()}`}
          >
            <Typography variant="body2">{row.configCreatedBy}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params) => [
          ...(params.row.reportId
            ? [
                <GridActionsCellItem
                  key="view"
                  icon={
                    <Tooltip title="View report details">
                      <OpenInNew />
                    </Tooltip>
                  }
                  label="View Report"
                  onClick={() =>
                    window.open(
                      `/staff/fulfillment/r/${params.row.reportId}`,
                      "_blank"
                    )
                  }
                />,
              ]
            : []),
          <GridActionsCellItem
            key="remove"
            icon={
              <Tooltip title="Remove from pilot program">
                <Close />
              </Tooltip>
            }
            label="Remove"
            onClick={() => onRemove(params.row.configId)}
            disabled={removeLoading}
          />,
        ],
      },
    ],
    [onRemove, removeLoading]
  );

  return (
    <Box sx={{ p: 2, pl: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Pilot Facilities ({pilotFacilities.length})
      </Typography>
      <DataGrid<PilotFacilityRow>
        rows={pilotFacilities}
        columns={columns}
        getRowId={(row) => row.configId}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-cell": {
            py: 1.5,
            alignItems: "center",
          },
          "& .MuiDataGrid-row": {
            minHeight: "52px !important",
          },
          // Show actions always (not just on hover)
          "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']":
            {
              opacity: 1,
            },
        }}
      />
    </Box>
  );
});
